import React, { useEffect } from "react";
import { useForm, validators, useFormSubmission } from "@hooks";
import { useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { helpers, Constants } from "@shared";

const QualificationInfo = () => {

    const navigate = useNavigate(), location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const {
        state: {
            account: {
                email,
                fullName
            }
        }
    } = location;

    const initialState = { email, fullName, last4SSN: `` };
    const validations = {
        email: (val) => validators.isValidEmail(val) || { email: `Email is not valid` },
        fullName: (val) => validators.hasValue(val) || { fullName: `Name is not valid` },
        last4SSN: (val) => validators.hasValue(val) && validators.hasNumber(val) && val.length === 4 || { last4SSN: `Last 4 digits must be exactly 4 digits` }
    };
    const { setFormState, hasFormError, formError, clearFormState } = useFormSubmission();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const handleSubmit = async (values) => {

        setSpinnerShow(true);
        clearFormState();
        try {
            const { last4SSN } = values;
            await sunboundPayProxy.qualificationInfo({ last4SSN });
            helpers.navigateNext(location, navigate);
        } catch (error) {
            setFormState(`Unable to process your request, please contact ${Constants.Help.Email} for support`);
        }
        setSpinnerShow(false);
    };

    const { values, errors, changeHandler, submitHandler } = useForm(initialState, {}, validations, handleSubmit);

    const onChangeNumber = (e) => {
        const re = /^[0-9\b]+$/;

        if (e.target.value === `` || re.test(e.target.value)) {
            changeHandler(e);
        }
    };

    return (
        <div className="page-content-wrapper">
            <h1 className="page-header">Confirm Your Personal Info</h1>
            <span className="page-subheader">This information is used to confirm your eligibility for<br />Sunbound&apos;s Pay Over Time offering</span>
            <form className=" w-full px-3" method="POST" onSubmit={submitHandler} >
                <div className="content-box shadow-sb w-full space-y-1">
                    <div className="w-full">
                        <label htmlFor="fullName" className="font-semibold text-gray-700">Name</label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                            <input type="text" name="fullName" id="fullName" className={classNames(`block w-full rounded-md shadow-md focus:outline-none text-gray-600 read-only:bg-gray-100`, { "input-error": errors.fullName })} value={values.fullName} readOnly={true} onChange={changeHandler} aria-invalid="true" aria-describedby="email-error" />
                            {
                                errors.fullName &&
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ExclamationCircleIcon className="fill-red-500 h-6" />
                                </div>
                            }
                        </div>
                        {
                            errors.fullName &&
                            <p className="mt-2 text-sm text-red-600" id="email-error">{errors.fullName}</p>
                        }
                    </div>
                    <div className="w-full">
                        <label htmlFor="email" className="font-semibold text-gray-700">Email</label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                            <input type="text" name="email" id="email" className={classNames(`block w-full rounded-md shadow-md focus:outline-none text-gray-600 read-only:bg-gray-100`, { "input-error": errors.email })} value={values.email} readOnly={true} onChange={changeHandler} aria-invalid="true" aria-describedby="email-error" />
                            {
                                errors.email &&
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ExclamationCircleIcon className="fill-red-500 h-6" />
                                </div>
                            }
                        </div>
                        {
                            errors.email &&
                            <p className="mt-2 text-sm text-red-600" id="email-error">{errors.email}</p>
                        }
                    </div>
                    <div className="w-full">
                        <label htmlFor="last4SSN" className="font-semibold text-gray-700">Last 4 digits of your SSN</label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                            <input type="text" name="last4SSN" id="last4SSN" maxLength="4" pattern="^[0-9]*$"
                                value={values.last4SSN} onChange={onChangeNumber} aria-invalid="true" aria-describedby="last4SSN-error"
                                onPaste={(e) => {
                                    e.preventDefault();
                                    return false;
                                }}
                                className={classNames(`block w-full rounded-md shadow-md focus:outline-none text-gray-600 read-only:bg-gray-100`, { "input-error": errors.last4SSN })} />
                            {
                                errors.last4SSN &&
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ExclamationCircleIcon className="fill-red-500 h-6" />
                                </div>
                            }
                        </div>
                        {
                            errors.last4SSN &&
                            <p className="mt-2 text-sm text-red-600" id="last4SSN-error">{errors.last4SSN}</p>
                        }
                        <p className="text-gray-500 pt-4"><b>Why do we ask for this?</b> It is used to confirm your eligibility for Sunbound and also required by US law to help fight the funding of terrorism and money laundering</p>
                    </div>
                </div>
                <div className="w-full pt-6">
                    <button type="submit" className="btn btn-primary">Continue</button>
                </div>
                {
                    hasFormError &&
                    <p className="text-red-600 text-xl">{formError}</p>
                }
            </form>
            <span className="text-gray-500">By clicking &quot;Continue&quot; I acknowledge Sunbound will check my credit history. <a href="https://www.sunboundhomes.com/terms-conditions" className="underline" target="_blank" rel="noreferrer">View Legal Details</a></span>
        </div>
    );
};

export default QualificationInfo;